import {
  iqEntityForType,
  iqMappingKeyFor,
  resolveRepeaterSubFields,
  type FieldDef,
  type FormType,
  type IqMappingEntry,
  type IqSource,
  type IqSystemKey,
} from '@jfh/intake-shared';

export type SourceKind = IqSource['type'];

export const IQ_SYSTEM_KEYS: { value: IqSystemKey; label: string }[] = [
  { value: 'submissionId', label: 'Submission ID' },
  { value: 'submittedAt', label: 'Submitted at' },
  { value: 'formType', label: 'Form type' },
  { value: 'formName', label: 'Form name' },
];

/** The four form types, in plain words: who approves it and what it makes in iQ. */
export const FORM_TYPE_CARDS: { value: FormType; label: string; desc: string }[] = [
  {
    value: 'trade_account',
    label: 'Trade account',
    desc: 'Customer Service approve it in Teams, then it becomes a customer in iQ.',
  },
  {
    value: 'credit_account',
    label: 'Credit account',
    desc: 'Two approvals: Customer Service, then Accounts decide the credit. Becomes a customer in iQ.',
  },
  {
    value: 'supplier_account',
    label: 'Supplier account',
    desc: 'The Supplier channel approve it, then it becomes a supplier in iQ.',
  },
  { value: 'other', label: 'Other (lead)', desc: 'No approval. Goes straight to iQ as a lead.' },
];

/** Teams channels a form's first approval can go to. */
export const STAGE1_CHANNELS = ['Customer Service', 'Accounts', 'Supplier'];

/** The channel the Worker uses when none is set (see CHANNEL_DEFAULTS). */
export function defaultChannel(formType: FormType): string {
  return formType === 'supplier_account' ? 'Supplier' : 'Customer Service';
}

/** What the approved application becomes in iQ. */
export function endText(formType: FormType): string {
  const entity = iqEntityForType(formType);
  return entity === 'supplier' ? 'Becomes a supplier in iQ' : entity === 'lead' ? 'Becomes a lead in iQ' : 'Becomes a customer in iQ';
}

/** Mappable questions under their step heading, for a grouped dropdown. */
export interface FieldGroup {
  title: string;
  fields: FieldDef[];
}

export function groupFieldsBySection(fields: FieldDef[]): FieldGroup[] {
  const groups: FieldGroup[] = [];
  let current: FieldGroup = { title: 'Form fields', fields: [] };
  for (const f of fields) {
    if (f.type === 'heading') {
      if (current.fields.length) groups.push(current);
      current = { title: f.label || 'Section', fields: [] };
    } else {
      current.fields.push(f);
    }
  }
  if (current.fields.length) groups.push(current);
  return groups;
}

/** A fresh source of each kind, pointing at a sensible question. */
export function defaultSourceForKind(kind: SourceKind, fields: FieldDef[]): IqSource {
  const first = fields.find((f) => f.type !== 'heading')?.id ?? '';
  if (kind === 'field') return { type: 'field', fieldId: first };
  if (kind === 'const') return { type: 'const', value: '', valueType: 'string' };
  if (kind === 'derived') return { type: 'derived', fieldId: first, rules: [], fallback: '' };
  if (kind === 'contact') {
    const cf = fields.find((f) => f.type === 'contact');
    const subs = cf ? resolveRepeaterSubFields(cf) : [];
    return { type: 'contact', fieldId: cf?.id ?? '', subFieldId: subs[0]?.id ?? '' };
  }
  if (kind === 'password') return { type: 'password' };
  if (kind === 'lookup') {
    const lf = fields.find((f) => f.optionsSource?.type === 'iq');
    return { type: 'lookup', fieldId: lf?.id ?? '', field: '' };
  }
  return { type: 'system', key: 'submissionId' };
}

/** Questions that get a row in the "Question → iQ field" table. */
export function tableQuestions(fields: FieldDef[], formType: FormType): FieldDef[] {
  return fields.filter(
    (f) =>
      !['heading', 'contact', 'repeater'].includes(f.type) && iqMappingKeyFor(f, formType) === 'iqMappings',
  );
}

/**
 * Split the main mappings: each table question's first direct mapping (shown
 * in its row) and everything else (constants, derived values, contact parts,
 * a second mapping of a question, a mapping of a deleted question), shown as
 * sentences. Returns the indexes of the "everything else" entries.
 */
export function otherMappingIndexes(entries: IqMappingEntry[], tableIds: Set<string>): number[] {
  const claimed = new Set<string>();
  const out: number[] = [];
  entries.forEach((m, i) => {
    if (m.source.type === 'field' && tableIds.has(m.source.fieldId) && !claimed.has(m.source.fieldId)) {
      claimed.add(m.source.fieldId);
      return;
    }
    out.push(i);
  });
  return out;
}

const SYSTEM_WORDS: Record<IqSystemKey, string> = {
  submissionId: 'the application’s reference',
  submittedAt: 'the date it was submitted',
  formType: 'the form type',
  formName: 'the form name',
};

/** One mapping as a sentence ending just before its iQ field, plus an icon. */
export function describeMapping(m: IqMappingEntry, fields: FieldDef[]): { icon: string; text: string } {
  const label = (id: string) => {
    const f = fields.find((x) => x.id === id);
    return f ? `“${f.label}”` : 'a deleted question';
  };
  const so = m.source;
  switch (so.type) {
    case 'const':
      return { icon: 'hash', text: `Always send “${so.value}” to` };
    case 'system':
      return { icon: 'clock', text: `Send ${SYSTEM_WORDS[so.key] ?? so.key} to` };
    case 'contact': {
      const cf = fields.find((x) => x.id === so.fieldId);
      const sub = cf ? resolveRepeaterSubFields(cf).find((x) => x.id === so.subFieldId) : undefined;
      return { icon: 'user', text: `Send the main contact’s ${sub ? sub.label.toLowerCase() : 'details'} to` };
    }
    case 'password':
      return { icon: 'shield', text: 'Make a website password on approval (also emailed to them) and send it to' };
    case 'derived':
      return { icon: 'filter', text: `Work out a value from ${label(so.fieldId)} and send it to` };
    case 'lookup':
      return { icon: 'db', text: `Send the ${so.field || 'extra value'} linked to the answer to ${label(so.fieldId)} to` };
    case 'field':
      return { icon: 'forms', text: `Send ${label(so.fieldId)} to` };
  }
}

/** The collapsed iQ card's one-line summary. */
export function iqSummary(mapped: number, questions: number, others: number, contactsAuto: boolean): string {
  const base = `${mapped} of ${questions} ${questions === 1 ? 'answer goes' : 'answers go'} to iQ`;
  const extra = others ? `, plus ${others} other ${others === 1 ? 'value' : 'values'}` : '';
  return `${base}${extra}.${contactsAuto ? ' Contacts go across automatically.' : ''}`;
}
